import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, AlertTriangle, KeyRound, Mail, Phone } from "lucide-react";
import { OtpBoxes } from "@/pages/VerifyEmail";

const Verified = () => <Badge className="rounded-sm bg-emerald-600 text-white gap-1"><CheckCircle2 className="h-3 w-3" /> Vérifié</Badge>;
const NotVerified = () => <Badge className="rounded-sm bg-amber-100 text-amber-700 gap-1"><AlertTriangle className="h-3 w-3" /> Non vérifié</Badge>;

export const SecurityPanel = () => {
  const [sec, setSec] = useState(null);
  const [dlg, setDlg] = useState(null); // password | email | email-code | phone | verify-email
  const load = () => api.get("/auth/security").then(({ data }) => setSec(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!sec) return null;

  return (
    <div className="mt-8 border border-border bg-card p-5" data-testid="security-panel">
      <h3 className="font-heading font-bold text-lg flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-accent" /> Sécurité</h3>
      <div className="mt-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2 border border-border p-3">
          <div className="flex items-center gap-2 text-sm"><Mail className="h-4 w-4 text-muted-foreground" />
            <span data-testid="sec-email">{sec.email}</span>
            {sec.email_verified ? <Verified /> : <NotVerified />}
            {sec.pending_email && <span className="text-xs text-muted-foreground">→ en attente : {sec.pending_email}</span>}
          </div>
          <div className="flex gap-2">
            {!sec.email_verified && <Button size="sm" variant="outline" className="rounded-sm text-xs" data-testid="sec-verify-email" onClick={() => setDlg("verify-email")}>Vérifier mon email</Button>}
            <Button size="sm" variant="outline" className="rounded-sm text-xs" data-testid="sec-change-email" onClick={() => setDlg(sec.pending_email ? "email-code" : "email")}>Modifier mon email</Button>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 border border-border p-3">
          <div className="flex items-center gap-2 text-sm"><Phone className="h-4 w-4 text-muted-foreground" />
            <span data-testid="sec-phone">{sec.phone || "Non renseigné"}</span>
            {sec.phone ? (sec.phone_verified ? <Verified /> : <NotVerified />) : null}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="rounded-sm text-xs" data-testid="sec-phone-btn" onClick={() => setDlg("phone")}>
              {sec.phone ? "Modifier mon téléphone" : "Ajouter un téléphone"}
            </Button>
            {sec.phone && !sec.phone_verified && (
              <Button size="sm" variant="outline" className="rounded-sm text-xs" data-testid="sec-verify-phone"
                onClick={async () => {
                  try { await api.post("/auth/phone/send-otp"); } catch (e) { toast.info(e.response?.data?.detail || "SMS non disponible"); }
                }}>Vérifier mon téléphone</Button>
            )}
          </div>
        </div>
        {sec.has_password && (
          <div className="flex flex-wrap items-center justify-between gap-2 border border-border p-3">
            <div className="flex items-center gap-2 text-sm"><KeyRound className="h-4 w-4 text-muted-foreground" /> Mot de passe</div>
            <Button size="sm" variant="outline" className="rounded-sm text-xs" data-testid="sec-change-password" onClick={() => setDlg("password")}>Modifier mon mot de passe</Button>
          </div>
        )}
      </div>

      <ChangePasswordDialog open={dlg === "password"} onClose={() => setDlg(null)} />
      <ChangeEmailDialog open={dlg === "email"} onClose={() => setDlg(null)} onCodeSent={() => setDlg("email-code")} />
      <EmailCodeDialog open={dlg === "email-code"} onClose={() => { setDlg(null); load(); }} pending={sec.pending_email} />
      <PhoneDialog open={dlg === "phone"} onClose={() => { setDlg(null); load(); }} current={sec.phone} />
      <VerifyEmailDialog open={dlg === "verify-email"} onClose={() => { setDlg(null); load(); }} email={sec.email} />
    </div>
  );
};

function ChangePasswordDialog({ open, onClose }) {
  const [f, setF] = useState({ current: "", pw: "", pw2: "" });
  const submit = async () => {
    if (f.pw !== f.pw2) { toast.error("Les mots de passe ne correspondent pas"); return; }
    try {
      const { data } = await api.post("/auth/change-password", { current_password: f.current, new_password: f.pw });
      toast.success(data.message); setF({ current: "", pw: "", pw2: "" }); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Modifier mon mot de passe</DialogTitle>
          <DialogDescription>Un email de confirmation vous sera envoyé.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Mot de passe actuel</Label><Input data-testid="cp-current" type="password" className="rounded-sm" value={f.current} onChange={(e) => setF({ ...f, current: e.target.value })} /></div>
          <div><Label>Nouveau mot de passe</Label><Input data-testid="cp-new" type="password" className="rounded-sm" value={f.pw} onChange={(e) => setF({ ...f, pw: e.target.value })} /><p className="mt-1 text-xs text-muted-foreground">8 caractères minimum.</p></div>
          <div><Label>Confirmation du nouveau mot de passe</Label><Input data-testid="cp-new2" type="password" className="rounded-sm" value={f.pw2} onChange={(e) => setF({ ...f, pw2: e.target.value })} /></div>
        </div>
        <DialogFooter><Button data-testid="cp-submit" className="rounded-sm bg-accent text-white" onClick={submit}>Modifier mon mot de passe</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChangeEmailDialog({ open, onClose, onCodeSent }) {
  const [f, setF] = useState({ email: "", password: "" });
  const submit = async () => {
    try {
      const { data } = await api.post("/auth/change-email/start", { new_email: f.email, password: f.password });
      toast.success(data.message); onCodeSent();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Modifier mon email</DialogTitle>
          <DialogDescription>Votre ancienne adresse reste active jusqu’à la validation de la nouvelle.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nouvelle adresse email</Label><Input data-testid="ce-email" type="email" className="rounded-sm" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div><Label>Mot de passe actuel</Label><Input data-testid="ce-password" type="password" className="rounded-sm" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
        </div>
        <DialogFooter><Button data-testid="ce-submit" className="rounded-sm bg-accent text-white" onClick={submit}>Envoyer le code de confirmation</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmailCodeDialog({ open, onClose, pending }) {
  const [code, setCode] = useState("");
  const submit = async () => {
    try {
      const { data } = await api.post("/auth/change-email/confirm", { code });
      toast.success(`Email modifié : ${data.email}`); setCode(""); onClose();
      window.location.reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Code invalide ou expiré"); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Confirmer ma nouvelle adresse</DialogTitle>
          <DialogDescription>Entrez le code reçu à {pending || "votre nouvelle adresse"}.</DialogDescription></DialogHeader>
        <OtpBoxes value={code} onChange={setCode} testid="ce-otp" />
        <DialogFooter><Button data-testid="ce-confirm" className="rounded-sm bg-accent text-white" onClick={submit}>Confirmer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PhoneDialog({ open, onClose, current }) {
  const [phone, setPhone] = useState(current || "");
  const submit = async () => {
    try {
      await api.post("/auth/phone", { phone });
      toast.success(phone ? "Téléphone enregistré (non vérifié — SMS bientôt disponible)" : "Téléphone supprimé");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{current ? "Modifier mon téléphone" : "Ajouter un numéro de téléphone"}</DialogTitle>
          <DialogDescription>Format international avec indicatif pays. Facultatif — utilisé plus tard pour les paiements mobiles et la livraison.</DialogDescription></DialogHeader>
        <div><Label>Numéro de téléphone</Label>
          <Input data-testid="ph-input" type="tel" placeholder="+225 0700000000" className="rounded-sm" value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
        <DialogFooter><Button data-testid="ph-submit" className="rounded-sm bg-accent text-white" onClick={submit}>Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VerifyEmailDialog({ open, onClose, email }) {
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const send = async () => {
    try { await api.post("/auth/verify-email/send", { email }); setSent(true); toast.success("Code envoyé"); }
    catch (e) { toast.error(e.response?.data?.detail || "Patientez avant de renvoyer"); }
  };
  const submit = async () => {
    try {
      await api.post("/auth/verify-email/confirm", { email, code });
      toast.success("Email vérifié"); setCode(""); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Code invalide ou expiré"); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Vérifier mon email</DialogTitle>
          <DialogDescription>Un code à 6 chiffres sera envoyé à {email}.</DialogDescription></DialogHeader>
        {!sent ? (
          <Button data-testid="ve-send" className="rounded-sm bg-primary text-white" onClick={send}>Envoyer le code</Button>
        ) : (
          <>
            <OtpBoxes value={code} onChange={setCode} testid="ve-otp" />
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={send}>Renvoyer</Button>
              <Button data-testid="ve-confirm" className="rounded-sm bg-accent text-white" onClick={submit}>Vérifier</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
